import {
  Button,
  Modal,
  Label,
  InputText,
  ErrorMessage,
  InputTextarea,
  Switch,
  IconButton,
} from '@teo/components';
import { CrossFilled, Warning } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { config } from '@/config';
import { axios } from '@/axios';
import { useGet } from '@/query';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { PaginationSingleSelect } from '@/components/PaginationSingleSelect';
import { newUid } from '@/util';
import {
  CompetenceValidityFields,
  defaultCompetenceExpiryDate,
  serializeCompetenceValidity,
} from './CompetenceValidityFields';
import { nextCompetenceCode } from './competenceCode';

const AddCompetenceModal = ({
  closeModal,
  setIsOpenAddModal,
  setRefetchTable,
  authUser,
  edit = false,
  setActiveCompetence,
  activeCompetence,
}) => {
  const { t } = useTranslation();
  const [checkValid, setCheckValid] = useState(false);
  const [autoCode, setAutoCode] = useState(true);
  const [addAsTeoCompetence, setAddAsTeoCompetence] = useState(
    Boolean(activeCompetence && activeCompetence?.organisationId == null)
  );
  const [code, setCode] = useState(activeCompetence?.code || '');
  const [nameCompetence, setNameCompetence] = useState(
    activeCompetence?.name || ''
  );
  const [description, setDescription] = useState(
    activeCompetence?.description || ''
  );

  const [categories, setCategories] = useState([]);
  const [selectCat, setSelectCat] = useState({
    id: '',
    name: '',
    value: '',
    color: '',
    code: '',
  });
  const [linkCompetencies, setLinkCompetencies] = useState([]);
  const [teoCompetencies, setTeoCompetencies] = useState([]);
  const [relatedCompetence, setRelatedCompetence] = useState([]);

  const [expireDuration, setExpireDuration] = useState(
    activeCompetence?.validForDays
      ? 'days'
      : activeCompetence?.expiryDate
      ? 'date'
      : 'forever'
  );
  const [validForDays, setValidForDays] = useState(
    activeCompetence?.validForDays ? activeCompetence?.validForDays : 90
  );

  const [expiryDate, setExpiryDate] = useState(
    activeCompetence?.expiryDate
      ? new Date(activeCompetence.expiryDate)
      : defaultCompetenceExpiryDate
  );

  const [serverErrors, setServerErrors] = useState({});

  useGet('/competencecategories', {
    organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
    lang: authUser?.lang,
    sort: 'name',
    onSuccess: (result) => {
      const allCat = result?.data;
      setCategories(
        allCat?.map((cat) => ({
          id: cat?.id,
          name: cat?.name,
          value: cat?.name,
          color: cat?.colorCode,
          code: cat?.codePrefix,
        })) || []
      );
      if (activeCompetence && activeCompetence?.competenceCategoryId) {
        const compCat = allCat?.find(
          (cat) => cat?.id === activeCompetence?.competenceCategoryId
        );
        if (compCat) {
          setSelectCat({
            id: compCat?.id,
            name: compCat?.name,
            value: compCat?.name,
            color: compCat?.colorCode,
            code: compCat?.codePrefix,
          });
        }
      }
    },
  });
  useGet(`/relatedcompetencedetails`, {
    enabled: edit,
    competenceId: activeCompetence?.competenceId,
    onSuccess: (result) => {
      setTeoCompetencies(result?.data || []);
      setRelatedCompetence(result?.data || []);
    },
  });

  const handleSelectCat = async (cat) => {
    setSelectCat(cat);

    if (cat?.id && autoCode) {
      const listCompt = await axios.get(
        `${config.backend}/categorycompetencedetails?competenceCategoryId=${cat.id}`
      );

      const arr = listCompt?.data || [];
      setCode(nextCompetenceCode(cat.code, arr));
    }
  };

  const handleSaveCompetence = async () => {
    setCheckValid(true);

    if (!nameCompetence || !code || !selectCat?.id) return;

    const validity = serializeCompetenceValidity({
      duration: expireDuration,
      expiryDate,
      validForDays,
    });
    if (edit) {
      const changes = {};
      const organisationId = addAsTeoCompetence
        ? null
        : activeCompetence?.organisationId ?? authUser?.activeOrganisationId;

      if (nameCompetence !== activeCompetence?.name) {
        changes.path = nameCompetence;
        changes.name = nameCompetence;
      }
      if (code !== activeCompetence?.code) {
        changes.code = code;
      }

      if (description !== activeCompetence?.description) {
        changes.description = description;
      }
      if (selectCat?.id !== activeCompetence?.competenceCategoryId) {
        changes.competenceCategoryId = selectCat?.id;
      }
      if (organisationId !== activeCompetence?.organisationId) {
        changes.organisationId = organisationId;
      }

      try {
        await axios.put(
          `${config.backend}/competence/${activeCompetence?.competenceId}`,
          {
            ...changes,
            ...validity,
          }
        );

        const competenceId = activeCompetence?.competenceId;

        const oldIds = new Set(
          (relatedCompetence || []).map((comp) => comp?.id).filter(Boolean)
        );

        const newIds = new Set(
          (teoCompetencies || []).map((comp) => comp?.id).filter(Boolean)
        );

        const toAdd = (teoCompetencies || []).filter(
          (comp) => !oldIds.has(comp?.id)
        );

        const toDelete = (relatedCompetence || []).filter(
          (comp) => !newIds.has(comp?.id)
        );

        await Promise.all([
          ...toAdd.map((comp) =>
            axios.post(`${config.backend}/competencerelations`, {
              competenceId,
              relatedCompetenceId: comp?.id,
            })
          ),

          ...toDelete.map((comp) =>
            axios.delete(
              `${config.backend}/competencerelation/${comp?.relationId}`
            )
          ),
        ]);

        setRefetchTable(newUid(20));
        setActiveCompetence(null);
        document.body.style.overflow = 'unset';
        setIsOpenAddModal(false);
      } catch (error) {
        console.error('Error saving competence:', error);
        handleServerErrors(error);
      }
    } else {
      try {
        const newComp = await axios.post(`${config.backend}/competences`, {
          path: nameCompetence,
          name: nameCompetence,
          organisationId: addAsTeoCompetence
            ? null
            : authUser?.activeOrganisationId,
          lang: authUser?.lang,
          code,
          description,
          competenceCategoryId: selectCat?.id,
          ...validity,
        });

        const competenceId = newComp?.data?.id;

        if (!competenceId) return;

        if (teoCompetencies?.length > 0) {
          await Promise.all(
            teoCompetencies.map((comp) =>
              axios.post(`${config.backend}/competencerelations`, {
                competenceId,
                relatedCompetenceId: comp?.id,
              })
            )
          );
        }

        setRefetchTable(newUid(20));
        document.body.style.overflow = 'unset';
        setIsOpenAddModal(false);
      } catch (error) {
        console.error('Error saving competence:', error);
        handleServerErrors(error);
      }
    }
  };

  const handleError = (el) => (el ? false : checkValid ? true : false);

  const handleAddLink = () => {
    const addCompetencies = [...teoCompetencies];
    for (const obj2 of linkCompetencies) {
      const existingObj = addCompetencies.find((obj1) => obj1.id === obj2.id);
      if (!existingObj) {
        addCompetencies.push(Object.assign(obj2, { role: 20 }));
      }
    }
    setTeoCompetencies(addCompetencies);
    setLinkCompetencies([]);
  };

  const handleRemove = (id) => {
    const removeTeam = teoCompetencies.filter((item) => item.id !== id);
    setTeoCompetencies(removeTeam);
  };

  const handleServerErrors = (error) => {
    const errors = error?.response?.data?.errors || [];

    const formattedErrors = errors.reduce((acc, item) => {
      if (item?.field) {
        acc[item.field] = item.message;
      }

      return acc;
    }, {});

    setServerErrors(formattedErrors);
  };

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-xl  rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            <h2 className="text-sm font-semibold">
              {edit
                ? t('pages.manage_competencies.edit_competence')
                : t('pages.manage_competencies.new_competence')}
            </h2>
          </Modal.Header>

          <Modal.Body>
            <div className="-ml-6 -mr-6 -mt-6 flex flex-col overflow-y-auto px-6 pt-6">
              <div className="flex flex-col gap-5">
                <div>
                  <h3 className="text-lg">
                    {edit
                      ? activeCompetence?.name
                      : t('pages.manage_competencies.add_competence')}
                  </h3>
                </div>

                <div className="flex w-full flex-col">
                  <div className="mb-1 text-sm text-black">
                    <Label htmlFor="competenceName">
                      {t('pages.manage_competencies.tab_categories.name')}
                    </Label>
                    <InputText
                      id="competenceName"
                      onChange={(e) => setNameCompetence(e.currentTarget.value)}
                      value={nameCompetence}
                      hasError={
                        handleError(nameCompetence) ||
                        Boolean(serverErrors.path)
                      }
                    />
                    {handleError(nameCompetence) && (
                      <ErrorMessage id="errorMessage">
                        {t('pages.onboarding.error.text_fiels')}
                      </ErrorMessage>
                    )}
                    {serverErrors.path && !handleError(nameCompetence) && (
                      <span
                        role="alert"
                        id="errorMessageName"
                        className="mt-1 flex items-center text-sm text-error-04"
                      >
                        <Warning className="mr-2 inline h-5 w-5" />
                        {t(
                          'pages.manage_competencies.competence_modal.erro_name'
                        )}
                      </span>
                    )}
                  </div>

                  {(authUser?.role || 0) >= 90 && (
                    <div
                      className="mb-1 mt-2 flex items-center justify-between gap-3 text-sm text-black"
                      data-testid="add-as-teo-competence-control"
                    >
                      <p className="text-grey-08">
                        {t(
                          'pages.manage_competencies.competence_modal.add_as_teo_competence'
                        )}
                      </p>
                      <Switch
                        checked={addAsTeoCompetence}
                        onChange={setAddAsTeoCompetence}
                      />
                    </div>
                  )}

                  <div className="relative z-[10] mb-1 mt-2 max-w-[290px] text-sm  text-black">
                    <Label htmlFor="categoryName">
                      {t('pages.manage_categories.title')}
                    </Label>
                    <div
                      className={`${
                        handleError(selectCat?.name) &&
                        'rounded-md border border-error-04 shadow-[0_0_1px_2px_rgba(254,224,214,1)] outline-error-01'
                      }`}
                    >
                      <PaginationSingleSelect
                        clearButtonLabel="clear"
                        onChange={async (cat) => {
                          handleSelectCat({
                            ...cat,
                            color: cat?.color || cat?.colorCode,
                            code: cat?.code || cat?.codePrefix,
                          });
                        }}
                        fetch={useGet.bind(null, '/competencecategories', {
                          organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
                          lang: authUser?.lang,
                        })}
                        defaultSort={[{ id: 'name', desc: false }]}
                        render={(category) => category?.name}
                        placeholder={`${t(
                          'pages.manage_competencies.import.select_category'
                        )}`}
                        value={selectCat?.name ? selectCat : undefined}
                        displayValue={(category) => category?.name}
                      >
                        {(option) => (
                          <span className="flex items-center">
                            <span
                              style={{
                                backgroundColor:
                                  option.color || option.colorCode,
                              }}
                              className={`mr-2 h-2 w-2 rounded-full`}
                            ></span>
                            {option.name}
                          </span>
                        )}
                      </PaginationSingleSelect>
                    </div>
                    {handleError(selectCat?.name) && (
                      <ErrorMessage id="errorMessage">
                        {t('pages.onboarding.error.text_fiels')}
                      </ErrorMessage>
                    )}
                  </div>

                  <div className=" mb-1 mt-2 w-full text-sm text-black">
                    <div className="mb-2 flex w-full items-center">
                      <p className="text-sm-semibold">
                        {t('pages.manage_competencies.competence_modal.code')}
                      </p>
                      <div className="ml-auto flex flex-row items-center gap-2">
                        <p className="text-grey-08">
                          {t(
                            'pages.manage_competencies.competence_modal.automatic_code'
                          )}
                        </p>
                        <Switch
                          checked={autoCode}
                          onChange={(isCheck) => setAutoCode(isCheck)}
                        />
                      </div>
                    </div>
                    <InputText
                      id="competenceCode"
                      onChange={(e) => setCode(e.currentTarget.value)}
                      value={code}
                      hasError={handleError(code) || Boolean(serverErrors.code)}
                      disabled={autoCode}
                    />
                    {handleError(code) && (
                      <ErrorMessage id="errorMessage">
                        {t('pages.onboarding.error.text_fiels')}
                      </ErrorMessage>
                    )}
                    {serverErrors.code && !handleError(code) && (
                      <span
                        role="alert"
                        id="errorMessageName"
                        className="mt-1 flex items-center text-sm text-error-04"
                      >
                        <Warning className="mr-2 inline h-5 w-5" />
                        {t(
                          'pages.manage_competencies.competence_modal.erro_code'
                        )}
                      </span>
                    )}
                  </div>

                  <div className="mb-1 mt-2 text-sm  text-black">
                    <Label htmlFor="descriptionCat">
                      {t(
                        'pages.manage_competencies.tab_categories.short_description'
                      )}
                    </Label>
                    <InputTextarea
                      id="descriptionCat"
                      placeholder={t(
                        `pages.create_edit_course.type_course.course_description_placeholder`
                      )}
                      onChange={(ev) => setDescription(ev.target.value)}
                      value={description}
                      limit={300}
                    />
                  </div>

                  <CompetenceValidityFields
                    duration={expireDuration}
                    expiryDate={expiryDate}
                    onDurationChange={setExpireDuration}
                    onExpiryDateChange={setExpiryDate}
                    onValidForDaysChange={setValidForDays}
                    validForDays={validForDays}
                  />
                  <div className="mb-1 mt-3 text-sm  text-black">
                    <Label htmlFor="linkCat">
                      {t(
                        'pages.manage_competencies.competence_modal.TEO_library'
                      )}
                    </Label>
                    <div className="ali relative z-[11] flex">
                      <span
                        className="w-full"
                        data-testid="organizationsSelect"
                      >
                        <PaginationMultiSelect
                          value={linkCompetencies || []}
                          onChange={(value) => {
                            setLinkCompetencies(value);
                          }}
                          keyFn={(row) => row?.id}
                          fetch={useGet.bind(null, '/competences', {
                            organisationId: 'null',
                            lang: authUser?.lang,
                            disabled: false,
                          })}
                          //  filterFn={(org) => {
                          //    return (
                          //      state.organizations.findIndex(
                          //        (x) => x.id === org.id
                          //      ) == -1
                          //    );
                          //  }}
                          defaultSort={[{ id: 'name', desc: false }]}
                          positionDropdown="top"
                          render={(row) => {
                            return row?.name;
                          }}
                        />
                      </span>

                      <Button
                        className="ml-3 h-[36px] text-base font-medium"
                        variant="primary"
                        disabled={linkCompetencies.length === 0}
                        onClick={handleAddLink}
                      >
                        {t('pages.course_content_flow.step_1.add_btn')}
                      </Button>
                    </div>
                    {teoCompetencies?.length > 0 && (
                      <div className="mt-2 flex flex-col gap-2">
                        {teoCompetencies?.map((comp) => {
                          return (
                            <div
                              key={`competence_${comp?.id}`}
                              className="flex items-center gap-2 rounded-md border p-2 text-sm"
                            >
                              <span className="w-[75px] min-w-max font-semibold text-grey-06">
                                {comp?.code}
                              </span>
                              <p className="max-w-[375px]">{comp?.name}</p>
                              <div className="ml-auto">
                                <IconButton
                                  Icon={CrossFilled}
                                  variant="ghost"
                                  onClick={() => handleRemove(comp.id)}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="mt-4 flex justify-between">
              <Button
                variant="ghost"
                onClick={() => {
                  document.body.style.overflow = 'unset';
                  setIsOpenAddModal(false);
                }}
              >
                {t('button.cancel')}
              </Button>
              <Button onClick={() => handleSaveCompetence()}>
                {edit ? t('button.edit') : t('button.add_btn')}
              </Button>
            </div> */}
          </Modal.Body>
          <Modal.Footer
            cancelButtonLabel={t('pages.create_edit_course.cancel_button')}
            onCancel={() => {
              closeModal();
            }}
          >
            <Button onClick={() => handleSaveCompetence()}>
              {edit ? t('button.edit') : t('button.add_btn')}
            </Button>
          </Modal.Footer>
        </div>
      </div>
    </div>
  );
};

export default AddCompetenceModal;
